import { RefObject, useEffect } from 'react';

type Handler = (event: MouseEvent | TouchEvent) => void;

/**
 * Custom hook that handles click outside of the passed ref
 */
export function useOnClickOutside<T extends HTMLElement = HTMLElement>(
  ref: RefObject<T | null> | T | null,
  handler: Handler,
  mouseEvent: 'mousedown' | 'mouseup' = 'mousedown',
  // Éléments rendus dans un portail (calendrier…) : hors du ref, mais un clic dedans reste « dedans »
  ignoreSelector?: string,
): void {
  useEffect(() => {
    if (!ref || !handler) {
      return;
    }

    const element = 'current' in ref ? ref.current : ref;

    if (!element) {
      return;
    }

    const listener = (event: MouseEvent | TouchEvent) => {
      // Do nothing if clicking ref's element or descendent elements
      if (!element || element.contains(event.target as Node)) {
        return;
      }

      if (ignoreSelector && (event.target as Element | null)?.closest?.(ignoreSelector)) {
        return;
      }

      handler(event);
    };

    document.addEventListener(mouseEvent, listener);
    document.addEventListener('touchstart', listener);

    return () => {
      document.removeEventListener(mouseEvent, listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [ref, handler, mouseEvent, ignoreSelector]);
}

export default useOnClickOutside;
